import React, { createRef, useMemo, useCallback } from 'react';
import {
  useDirsActions,
  useLoadedDirs,
} from '../../../../../modules/dirs/hooks';
import { SortableTree } from 'components';
import { map, getTreeFromFlatData } from '@nosferatu500/react-sortable-tree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DirectoryLabel } from './DirectoryLabel';
import FolderIcon from '@mui/icons-material/Folder';
import { Dir } from '../../../../../modules/dirs';
import { useUserHasRoles } from '../../../../../modules/auth/hooks';
import { ROLES } from '../../../../../modules/auth';
import { useNavigate } from 'utils/router';
import { useDrawerContext } from '../../../../../views/MyDisk/DrawerContext';
import { RESOURCE_TYPE } from '../../../../../modules/resources';
import { Box } from '@mui/material';

const classes = {
  root: {
    paddingBottom: 1,
  },
  icon: {
    color: 'text.secondary',
    marginRight: 1,
  },
};

type Props = {
  maxHeight?: number;
};

export function DirectoryTree({ maxHeight }: Props) {
  const navigateTo = useNavigate();
  const { fetchDirs, readDir, moveDir } = useDirsActions();
  const [expanded, setExpanded] = React.useState<Dir[]>([]);
  const { dirs: flatData, isLoading } = useLoadedDirs();
  const canDrag = useUserHasRoles([ROLES.ROLE_ADMIN, ROLES.ROLE_SUPER_ADMIN]);
  const containerRef = createRef<HTMLDivElement>();
  const { setSelection: setDrawerSelection } = useDrawerContext();

  const treeData = useMemo(() => {
    return getTreeFromFlatData({
      // @ts-ignore
      flatData,
      getParentKey: (node) => {
        // @ts-ignore
        return node.parentId || '0';
      },
    });
  }, [flatData]);

  const { data, visibleLength } = useMemo(() => {
    let visibleLength = treeData.length;

    const data = map({
      treeData,
      getNodeKey: ({ node }) => node.id,
      ignoreCollapsed: false,
      callback: ({ node }: any) => {
        if (expanded.find((item) => item.id === node.id)) {
          node.expanded = true;
          if (node.children) {
            visibleLength += node.children.length;
          }
        }

        return node;
      },
    });

    return { data, visibleLength };
  }, [treeData, expanded]);

  const handleNodeExpand = (dir: Dir) => {
    readDir({ id: dir.id });
    fetchDirs({
      parentId: dir.id,
    });
  };

  const collectNested = (item: Dir): number[] => {
    return [item.id].concat(...(item.children || []).map(collectNested));
  };

  const handleNodeCollapse = (dir: Dir) => {
    const children = collectNested(dir);

    setExpanded((prev) => prev.filter((item) => !children.includes(item.id)));
  };

  const handleDirMove = useCallback(
    ({ node, nextParentNode, treeData }: any) => {
      if (nextParentNode) {
        setExpanded((prev) => [...prev, nextParentNode]);
      }

      let position = -1;
      const nodes = nextParentNode ? nextParentNode.children : treeData;
      nodes.find((item: Dir) => {
        position++;
        return +item.id === +node.id;
      });

      const siblings: any = {};
      let siblingPosition = -1;
      nodes.forEach((node: Dir) => {
        siblingPosition++;
        siblings[node.id] = siblingPosition;
      });

      moveDir({
        id: node.id,
        parent: nextParentNode ? nextParentNode.id : null,
        position,
        siblings,
      });
    },
    [moveDir],
  );

  const navigate = useCallback(
    (node: Dir) => {
      readDir({ id: node.id });
      navigateTo(`/directory/${node.id}`);
      setDrawerSelection({
        ...node,
        id: node.id,
        type: RESOURCE_TYPE.DIRECTORY,
      });
    },
    [readDir, setDrawerSelection],
  );

  if (isLoading) {
    return null;
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <Box sx={classes.root} ref={containerRef}>
      <SortableTree
        visibleLength={visibleLength}
        isVirtualized
        treeData={data}
        canDrag={canDrag}
        maxHeight={maxHeight}
        onVisibilityToggle={({ node, expanded }) => {
          if (expanded) {
            setExpanded((prev) => [...prev, node as Dir]);
            handleNodeExpand(node as Dir);
          } else {
            handleNodeCollapse(node as Dir);
          }
        }}
        onMoveNode={handleDirMove}
        getNodeKey={({ node }) => node.id}
        generateNodeProps={() => ({
          collapseIcon: ExpandMoreIcon,
          expandIcon: ChevronRightIcon,
          nodeHasChildren: (node: Dir) => {
            if (!node) {
              return false;
            }

            if (node.children) {
              return node.children.length > 0;
            }

            return node.childrenCount > 0;
          },
          title: ({ node, isDragging }: any) => (
            <DirectoryLabel directory={node}>
              {isDragging ? (
                <>
                  <FolderIcon color="inherit" sx={classes.icon} />
                  <span>{node.title}</span>
                </>
              ) : (
                // eslint-disable-next-line
                <a onClick={() => navigate(node)}>
                  <FolderIcon color="inherit" sx={classes.icon} />
                  <span>{node.title}</span>
                </a>
              )}
            </DirectoryLabel>
          ),
        })}
      />
    </Box>
  );
}
